import { useEffect, useState } from "react";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useUser } from "../useHook";
import Swal from "sweetalert2";
import Loader, { ImageWithFallback } from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { FiEdit2, FiFilter, FiRotateCcw, FiSearch } from "react-icons/fi";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function Users() {
  const { getAllUsers, updateUserStatus, searchUsers } = useUser();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [usersData, setUsersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingUserId, setLoadingUserId] = useState(null); // Track which user is being updated

  const { hasPermission } = usePermission();
  const canUpdateUserStatus = hasPermission(SystemPermissions.Users.TOGGLE);
  const canViewDetails = hasPermission(SystemPermissions.Users.VIEW);
  console.log("aksjhdkjhasldk  ", canViewDetails);
  // Pagination state
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 9,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);

  const handleSearch = async () => {
    try {
      const response = await searchUsers(
        searchQuery,
        emailQuery,
        statusFilter,
        offset,
        limit,
      );
      setUsersData(response?.users || []);
      setPagination({
        totalRecords: response?.pagination?.totalRecords || 0,
        totalPages: response?.pagination?.totalPages || 0,
        currentPage: response?.pagination?.currentPage || 1,
        perPage: response?.pagination?.perPage || limit,
      });
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleReset = () => {
    setSearchQuery("");
    setEmailQuery("");
    setStatusFilter("");
    setOffset(0);
    getAllUsers(setUsersData, 0, limit);
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await getAllUsers(setUsersData, offset, limit);
        if (response?.pagination) {
          setPagination({
            totalRecords: response.pagination.totalRecords,
            totalPages: response.pagination.totalPages,
            currentPage: response.pagination.currentPage,
            perPage: response.pagination.perPage,
          });
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [offset]);

  const handleShowDetails = (user) => {
    navigate("/users/view", { state: user });
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setLoadingUserId(id);
    try {
      const newStatus = !currentStatus;
      const response = await updateUserStatus(id, newStatus);

      if (!response?.success) {
        throw new Error(response?.message || "Failed to update user status.");
      }

      setUsersData((prevUser) =>
        prevUser.map((user) =>
          user.id === id ? { ...user, status: newStatus } : user,
        ),
      );

      Swal.fire({
        title: "Success",
        text:
          response.message ||
          `User status has been ${
            newStatus ? "activated" : "deactivated"
          } successfully!`,
        icon: "success",
        timer: 1000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update user status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setLoadingUserId(null);
    }
  };

  // Show loader while loading initially
  if (isLoading) {
    return <Loader message="Loading users..." />;
  }

  return (
    <div>
      <div>
        <h2 className="text-2xl font-bold">Users</h2>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="search-wrapper">
          <input
            type="email"
            className="search-input"
            placeholder="Search by email"
            value={emailQuery}
            onChange={(e) => setEmailQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>

        <select
          className="status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Status
          </option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>
      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                {/* <th className="table-th">Image</th> */}
                <th className="table-th">Name</th>
                <th className="table-th">Email</th>
                <th className="table-th">Status</th>
                {canViewDetails && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {usersData.length === 0 ? (
                <tr>
                  <td
                    colSpan={canViewDetails ? 5 : 4}
                    className="text-center py-8"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                usersData.map((user, index) => (
                  <tr
                    key={user.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    {/* <td className="table-td">
                    <ImageWithFallback
                      src={user.profileImage}
                      alt={user.firstName}
                      className="w-14 h-14 object-cover rounded-full shadow-lg"
                    />
                  </td> */}
                    <td className="table-td">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="table-td">{user.email}</td>
                    <td className="table-td">
                      {canUpdateUserStatus ? (
                        <div
                          className={`status-toggle ${user.status ? "active" : ""} ${
                            loadingUserId === user.id ? "loading" : ""
                          }`}
                          onClick={() =>
                            loadingUserId !== user.id &&
                            handleToggleStatus(user.id, user.status)
                          }
                        >
                          <div className="status-toggle-handle"></div>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            user.status
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {user.status ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    {canViewDetails && (
                      <td className="table-td">
                        <button
                          className="btn btn-view text-white"
                          onClick={() => handleShowDetails(user)}
                        >
                          <IoEye /> View
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Component */}
      {pagination.totalPages > 1 && (
        <div className="mt-5">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={pagination.totalRecords}
            onPageChange={handlePageChange}
            className=""
          />
        </div>
      )}
    </div>
  );
}

export default Users;
